import React, { useEffect, useState } from "react";
import axios from "axios";
import { emptyTier, hasTierErrors, validateTiers } from "../utils/pricingTierUtil";

function AddSeasonModal({ leagueId, leagueBanner, onSeasonAdded }) {
    const [name, setName] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [registrationStartDate, setRegistrationStartDate] = useState("");
    const [registrationDeadline, setRegistrationDeadline] = useState("");
    const [banner, setBanner] = useState(null);
    const [bannerPreview, setBannerPreview] = useState(leagueBanner || null);
    const [tiers, setTiers] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Keep the preview defaulted to the league banner until the user picks their own file.
    useEffect(() => {
        if (!banner) {
            setBannerPreview(leagueBanner || null);
        }
    }, [leagueBanner, banner]);

    const handleBannerChange = (e) => {
        const file = e.target.files[0] || null;
        setBanner(file);
        setBannerPreview(file ? URL.createObjectURL(file) : leagueBanner || null);
    };

    const addTier = () => setTiers((prev) => [...prev, emptyTier(prev.length)]);

    const removeTier = (idx) => setTiers((prev) => prev.filter((_, i) => i !== idx));

    const updateTier = (idx, field, value) =>
        setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));

    const resetForm = () => {
        setName("");
        setStartDate("");
        setEndDate("");
        setRegistrationStartDate("");
        setRegistrationDeadline("");
        setBanner(null);
        setBannerPreview(leagueBanner || null);
        setTiers([]);
        setFieldErrors({});
        setError(null);
    };

    // This modal is raw Bootstrap markup and never unmounts, so state survives a
    // close. Reset on cancel too, or abandoned tier rows reappear on next open.
    const handleCancel = () => resetForm();

    const tierErrors = fieldErrors.pricing_tiers || [];
    const errorText = (value) => (Array.isArray(value) ? value.join(" ") : value);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setFieldErrors({});

        if (tiers.length > 0) {
            const missingWindow = {};
            if (!registrationStartDate) {
                missingWindow.registration_start_date = ["Required when pricing tiers are defined."];
            }
            if (!registrationDeadline) {
                missingWindow.registration_deadline = ["Required when pricing tiers are defined."];
            }

            const rowErrors = validateTiers(tiers, registrationStartDate, registrationDeadline);
            if (Object.keys(missingWindow).length || hasTierErrors(rowErrors)) {
                setFieldErrors({ ...missingWindow, pricing_tiers: rowErrors });
                return;
            }
        }

        setLoading(true);

        const formData = new FormData();
        formData.append("league", leagueId);
        formData.append("name", name);
        formData.append("start_date", startDate);
        formData.append("end_date", endDate);
        formData.append("registration_start_date", registrationStartDate);
        formData.append("registration_deadline", registrationDeadline);
        if (tiers.length > 0) {
            formData.append("pricing_tiers", JSON.stringify(tiers));
        }

        if (banner) {
            formData.append("banner", banner);
        } else if (leagueBanner) {
            // Ask the server to copy the league's banner. It can't be fetched
            // here: in production media is served from S3, a different origin,
            // and the browser blocks reading those bytes without a CORS rule.
            formData.append("use_league_banner", "true");
        }

        try {
            const response = await axios.post("/api/leagues/add-season/", formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            onSeasonAdded(response.data.season);
            resetForm();
            document.getElementById("closeAddSeasonModal").click(); // Close modal
        } catch (err) {
            console.error("Error adding season:", err);
            const responseErrors = err.response?.data?.errors;
            if (responseErrors) {
                setFieldErrors(responseErrors);
                setError("Please correct the highlighted fields.");
            } else {
                setError("Failed to add season. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal fade" id="addSeasonModal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add New Season</h5>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleCancel}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label htmlFor="seasonName" className="form-label">Season Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="seasonName"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label htmlFor="startDate" className="form-label">Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        id="startDate"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label htmlFor="endDate" className="form-label">End Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        id="endDate"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label htmlFor="registrationStartDate" className="form-label">
                                        Registration Start Date
                                    </label>
                                    <input
                                        type="date"
                                        className={`form-control ${fieldErrors.registration_start_date ? "is-invalid" : ""}`}
                                        id="registrationStartDate"
                                        value={registrationStartDate}
                                        onChange={(e) => setRegistrationStartDate(e.target.value)}
                                    />
                                    {fieldErrors.registration_start_date && (
                                        <div className="invalid-feedback d-block small">
                                            {errorText(fieldErrors.registration_start_date)}
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label htmlFor="registrationDeadline" className="form-label">
                                        Registration Deadline
                                    </label>
                                    <input
                                        type="date"
                                        className={`form-control ${fieldErrors.registration_deadline ? "is-invalid" : ""}`}
                                        id="registrationDeadline"
                                        value={registrationDeadline}
                                        onChange={(e) => setRegistrationDeadline(e.target.value)}
                                    />
                                    {fieldErrors.registration_deadline && (
                                        <div className="invalid-feedback d-block small">
                                            {errorText(fieldErrors.registration_deadline)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Pricing Tiers */}
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="form-label mb-0">Pricing Tiers</label>
                                    <button
                                        type="button"
                                        className="btn btn-link text-secondary p-0 rounded-circle"
                                        onClick={addTier}
                                        title="Add pricing tier"
                                    >
                                        <i className="bi bi-plus-circle fs-5"></i>
                                    </button>
                                </div>

                                {tiers.length === 0 ? (
                                    <div className="text-muted small">
                                        No pricing tiers. Registration will have no fee until one is added.
                                    </div>
                                ) : (
                                    tiers.map((tier, idx) => {
                                        const rowError = tierErrors[idx] || {};
                                        return (
                                            <div className="row g-2 align-items-start mb-2" key={idx}>
                                                <div className="col-12 col-md-3">
                                                    {idx === 0 && <label className="form-label small mb-1">Name</label>}
                                                    <input
                                                        type="text"
                                                        className={`form-control form-control-sm ${rowError.name ? "is-invalid" : ""}`}
                                                        value={tier.name}
                                                        onChange={(e) => updateTier(idx, "name", e.target.value)}
                                                        placeholder="Name"
                                                    />
                                                    {rowError.name && (
                                                        <div className="invalid-feedback d-block small">
                                                            {errorText(rowError.name)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-6 col-md-3">
                                                    {idx === 0 && <label className="form-label small mb-1">Start</label>}
                                                    <input
                                                        type="date"
                                                        className={`form-control form-control-sm ${rowError.start_date ? "is-invalid" : ""}`}
                                                        value={tier.start_date}
                                                        onChange={(e) => updateTier(idx, "start_date", e.target.value)}
                                                    />
                                                    {rowError.start_date && (
                                                        <div className="invalid-feedback d-block small">
                                                            {errorText(rowError.start_date)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-6 col-md-3">
                                                    {idx === 0 && <label className="form-label small mb-1">End</label>}
                                                    <input
                                                        type="date"
                                                        className={`form-control form-control-sm ${rowError.end_date ? "is-invalid" : ""}`}
                                                        value={tier.end_date}
                                                        onChange={(e) => updateTier(idx, "end_date", e.target.value)}
                                                    />
                                                    {rowError.end_date && (
                                                        <div className="invalid-feedback d-block small">
                                                            {errorText(rowError.end_date)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-8 col-md-2">
                                                    {idx === 0 && <label className="form-label small mb-1">Fee</label>}
                                                    <div className="input-group input-group-sm">
                                                        <span className="input-group-text">$</span>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            className={`form-control ${rowError.fee ? "is-invalid" : ""}`}
                                                            value={tier.fee}
                                                            onChange={(e) => updateTier(idx, "fee", e.target.value)}
                                                        />
                                                    </div>
                                                    {rowError.fee && (
                                                        <div className="invalid-feedback d-block small">
                                                            {errorText(rowError.fee)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-4 col-md-1">
                                                    {idx === 0 && <label className="form-label small mb-1 d-block">&nbsp;</label>}
                                                    <button
                                                        type="button"
                                                        className="btn btn-link text-danger p-0"
                                                        onClick={() => removeTier(idx)}
                                                        title="Remove tier"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            <div className="mb-3">
                                <label htmlFor="banner" className="form-label">Banner</label>
                                {bannerPreview && (
                                    <div className="mb-2">
                                        <img
                                            src={bannerPreview}
                                            alt="Season banner preview"
                                            className="img-fluid rounded"
                                            style={{ maxHeight: "150px", objectFit: "cover" }}
                                        />
                                        {!banner && (
                                            <div className="form-text">
                                                Defaults to the league's banner. Choose a file to override it.
                                            </div>
                                        )}
                                    </div>
                                )}
                                <input
                                    type="file"
                                    className="form-control"
                                    id="banner"
                                    onChange={handleBannerChange}
                                />
                            </div>
                            {error && <div className="text-danger mb-3">{error}</div>}
                            <div className="d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    data-bs-dismiss="modal"
                                    id="closeAddSeasonModal"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    {loading ? "Adding..." : "Add Season"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddSeasonModal;
